/**
 * formDeriveOperandPaths
 *
 * The canonical operand-path spelling of the derive engine, in one place because
 * it is persisted twice over: as a stamp's `inputs` array and as the prefix of
 * every fingerprint material line. `resolveFormDeriveInputs` sorts and dedupes
 * what this returns; `computeFormDerives` prefixes its hash lines with it. A
 * second spelling would let one stamp's `inputs` and its `hash` disagree about
 * the very same operand, so every spelling below is FROZEN once it has shipped.
 * Internal to this directory (precedent: `isRecordValue`) — the barrel exports
 * the two functions built on it, never this primitive.
 *
 * Two arms yield TWO paths, identity FIRST — `referenceProp` and
 * `rowReferenceProp` — the reference itself, then the record property it
 * resolves to, and the identity alone when nothing resolves. `groupKey` and
 * `cases` yield NONE: the key's path is the entry's own `colUniques` source, and
 * a guard reads earlier columns whose paths this list already carries. Every
 * other source yields exactly one.
 */

import { arTableCollectionName } from '../System.Artifacts.types';
import type { ArColumnAggregation, ArTableId } from '../System.Artifacts.types';
import type {
  FormDeriveColUniquesSource,
  FormDeriveReferenceProp,
  FormDeriveRowValueSource,
  FormDeriveScalarSource,
} from '../System.FormDerive.types';
import type { EntityReferences } from '../System.types';

/**
 * One row-projection column arm carried with the two facts it cannot carry
 * itself: the entry's source `tableId`, and — for `rowReferenceProp` alone — the
 * docId the winner row's reference cell names. A caller that omits
 * `entityRecordId` gets the identity path only, which is exactly what a
 * config-shaped `inputs` array may name.
 */
export type FormDeriveRowValueOperand = FormDeriveRowValueSource & {
  tableId: ArTableId;
  entityRecordId?: string;
};

/** Every `source` shape an entry declares: scalar operands, the partition key, row-value arms. */
export type FormDeriveOperandSource =
  | FormDeriveScalarSource
  | FormDeriveColUniquesSource
  | FormDeriveRowValueOperand;

/** `name` | `fields.<fieldId>` | `entityReferences.<referenceKey>`. */
function referencePropPath(prop: FormDeriveReferenceProp): string {
  if (prop.kind === 'name') return 'name';
  if (prop.kind === 'field') return `fields.${prop.fieldId}`;
  return `entityReferences.${prop.referenceKey}`;
}

/**
 * The fold spelling, shared by the whole-table `colAggregate` and the per-group
 * `groupAggregate`: one path means one (column, sub-path, fold), and the
 * material lines under it say which rows were folded.
 */
function aggregatePath(
  column: string,
  aggregation: ArColumnAggregation,
  aggregationFieldPath: string | undefined,
): string {
  const fieldPath =
    aggregationFieldPath === undefined || aggregationFieldPath === ''
      ? ''
      : `[.${aggregationFieldPath}]`;
  return `${column}${fieldPath}[${aggregation}]`;
}

function formDeriveOperandPaths(
  source: FormDeriveOperandSource,
  entityReferences: EntityReferences | undefined,
): string[] {
  if (source.kind === 'field') return [`fields.${source.fieldId}`];
  if (source.kind === 'groupKey' || source.kind === 'cases') return [];

  if (source.kind === 'referenceProp') {
    const identity = `entityReferences.${source.referenceKey}`;
    const reference = entityReferences?.[source.referenceKey];
    const entityRecordId = reference?.entityRecordId;
    if (entityRecordId === undefined || entityRecordId === '') return [identity];
    const entityId = reference?.entityId ?? source.referenceKey;
    return [identity, `entityRecords.${entityId}.${entityRecordId}.${referencePropPath(source.prop)}`];
  }

  if (source.kind === 'rowReferenceProp') {
    const propPath = referencePropPath(source.prop);
    const identity = `${arTableCollectionName(source.tableId)}.${source.referenceTypeId}[ref:${propPath}]`;
    const { entityRecordId } = source;
    if (entityRecordId === undefined || entityRecordId === '') return [identity];
    return [identity, `entityRecords.${entityRecordId}.${propPath}`];
  }

  const column = `${arTableCollectionName(source.tableId)}.${source.typeId}`;
  if (source.kind === 'colUniques') return [`${column}[uniques]`];
  if (source.kind === 'sourceCell') return [`${column}[winner]`];
  return [aggregatePath(column, source.aggregation, source.aggregationFieldPath)];
}

export { formDeriveOperandPaths };
